import type { Persistor } from 'redux-persist';
import { postActivityBeacon } from '@/apis/activity';
import type {
  BeaconEvent,
  BeaconResponseData,
  TrackedBeaconEvent,
} from '@/apis/types';
import { BeaconEventType } from '@/common/enum';
import { APIPaths } from '@/common/constants';
import { baseUrl } from '@/common/interceptor';
import { getActiveToken, isPreviewSession } from '@/common/helper/tokenStorage';
import {
  eventAdded,
  eventsRemoved,
} from '@/redux-toolkit/slice/activityBeaconSlice';
import type { AppDispatch, RootState } from '@/redux-toolkit/store';

// Page-view/route capture is enabled -- cleared by product/security review.

const FLUSH_DEBOUNCE_MS = 5000;
// A search is recorded once the user stops typing for this long, so typing
// "maersk" is one event rather than "m", "ma", "mae", ...
const SEARCH_SETTLE_MS = 1000;

interface BeaconStore {
  getState: () => RootState;
  dispatch: AppDispatch;
}

// Handed in by initActivityBeacon (from index.tsx) instead of imported:
// store -> slices -> apis -> interceptor -> this file would be a cycle.
let beaconStore: BeaconStore | null = null;
let beaconPersistor: Persistor | null = null;

let lastRoute: string | null = null;
let lastSearchQuery: string | null = null;
let heldSearch: { query: string; occurredAt: string } | null = null;
let searchTimer: ReturnType<typeof setTimeout> | null = null;
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let sending = false;

// Preview sessions are suppressed — those actions are the admin exploring a
// role, and a preview token would attribute any send to the previewed user.
function isTrackingDisabled(): boolean {
  return isPreviewSession();
}

function pendingEvents(): TrackedBeaconEvent[] {
  return beaconStore?.getState().activityBeacon.pending ?? [];
}

/** The backend will reject this whole batch every time, so retrying would
 * only keep it stuck at the front of the queue. */
function isRejectedForGood(error: unknown): boolean {
  const status = (error as { response?: { status?: number } } | null)?.response
    ?.status;
  return status === 400 || status === 403 || status === 413;
}

/**
 * Ids the backend has answered for: accepted (logged now or already logged)
 * plus rejected (can never be logged). Anything it didn't list stays queued.
 * A backend that predates the accepted/rejected contract answers with
 * neither list — then everything sent is treated as accepted, as before.
 */
function answeredIds(
  data: BeaconResponseData | undefined,
  sentIds: string[]
): string[] {
  const accepted = data?.accepted_ids;
  const rejected = data?.rejected_ids;
  if (!Array.isArray(accepted) && !Array.isArray(rejected)) return sentIds;
  return [...(accepted ?? []), ...(rejected ?? [])];
}

function clearFlushTimer(): void {
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
}

function clearSearchTimer(): void {
  if (searchTimer !== null) {
    clearTimeout(searchTimer);
    searchTimer = null;
  }
}

function scheduleFlush(): void {
  if (flushTimer !== null) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flush();
  }, FLUSH_DEBOUNCE_MS);
}

function enqueue(event: BeaconEvent): void {
  if (!beaconStore || isTrackingDisabled()) return;
  beaconStore.dispatch(eventAdded(event));
  scheduleFlush();
}

/** Records the held search (if any) now, instead of waiting for the user
 * to stop typing. */
function commitHeldSearch(): void {
  clearSearchTimer();
  const held = heldSearch;
  heldSearch = null;
  if (!held || held.query === lastSearchQuery) return;
  lastSearchQuery = held.query;
  enqueue({
    event_type: 'search',
    occurred_at: held.occurredAt,
    query: held.query,
  });
}

/**
 * Sends everything pending. Only the ids the backend answers for are
 * removed — on a network/server error everything stays queued (and
 * persisted) for the next flush or the next page load. Never throws.
 */
function flush(): void {
  const batch = pendingEvents();
  if (!beaconStore || sending || batch.length === 0 || isTrackingDisabled()) {
    return;
  }
  sending = true;
  const sentIds = batch.map((event) => event.tracking_id);

  postActivityBeacon(batch)
    .then((res) => {
      beaconStore?.dispatch(
        eventsRemoved(answeredIds(res?.data?.data, sentIds))
      );
      // Events tracked while this request was in flight. Ids the backend
      // left unanswered wait for the next trigger rather than looping.
      const sent = new Set(sentIds);
      if (pendingEvents().some((e) => !sent.has(e.tracking_id))) {
        scheduleFlush();
      }
    })
    .catch((error: unknown) => {
      if (isRejectedForGood(error)) {
        beaconStore?.dispatch(eventsRemoved(sentIds));
      }
      // Otherwise keep them — no retry loop; the next tracked event or the
      // next page load sends them again.
    })
    .finally(() => {
      sending = false;
    });
}

/**
 * Sends everything pending right now, in a way that survives the page going
 * away (keepalive fetch — unlike sendBeacon it can carry the Authorization
 * header). Used on tab hide/close and before a logout/session switch.
 *
 * Nothing is removed here: we can't see the response once the page is gone,
 * so the events stay persisted and are sent again on the next load — the
 * backend recognises their tracking_id and doesn't log them twice.
 */
export function sendPendingActivityNow(): void {
  commitHeldSearch();
  const batch = pendingEvents();
  if (batch.length === 0 || isTrackingDisabled()) return;
  clearFlushTimer();

  // Make sure the queue is written to storage before the tab goes away.
  beaconPersistor?.flush().catch(() => {});

  const token = getActiveToken();
  if (!token || typeof fetch === 'undefined') return;
  fetchActivityBeacon(batch, token, { keepalive: true }).catch(() => {
    // Best-effort: the events are still persisted for the next load.
  });
}

// Plain fetch, not postActivityBeacon: these paths need keepalive/abort, which axios can't do here.
function fetchActivityBeacon(
  batch: TrackedBeaconEvent[],
  token: string,
  init: RequestInit
): Promise<Response> {
  return fetch(`${baseUrl}${APIPaths.activityBeacon}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(batch),
    ...init,
  });
}

/** Logout awaits this so the last events go out before the token is removed. */
export async function flushActivityBeforeLogout(): Promise<void> {
  commitHeldSearch();
  clearFlushTimer();
  const batch = pendingEvents();
  const token = getActiveToken();
  if (!batch.length || !token || isTrackingDisabled()) return;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 3000);
  try {
    const response = await fetchActivityBeacon(batch, token, {
      signal: controller.signal,
    });
    if (!response.ok) throw new Error('Activity flush rejected');
    const result = await response.json();
    beaconStore?.dispatch(
      eventsRemoved(
        answeredIds(
          result?.data,
          batch.map((event) => event.tracking_id)
        )
      )
    );
  } catch {
    console.warn('Pending activity could not be confirmed before logout.');
  } finally {
    clearTimeout(timeout);
  }
}

function onVisibilityChange(): void {
  if (document.visibilityState === 'hidden') {
    sendPendingActivityNow();
  }
}

/**
 * Connects the beacon to the Redux store and sends anything left over from a
 * previous page load once redux-persist has restored it. Call once at app
 * startup.
 */
export function initActivityBeacon(
  store: BeaconStore,
  persistor: Persistor
): void {
  if (beaconStore) return;
  beaconStore = store;
  beaconPersistor = persistor;

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', sendPendingActivityNow);
  }

  if (persistor.getState().bootstrapped) {
    flush();
    return;
  }
  const unsubscribe = persistor.subscribe(() => {
    if (persistor.getState().bootstrapped) {
      unsubscribe();
      flush();
    }
  });
}

/**
 * Records a page view for the current route. Deduplicates consecutive calls
 * for the same route. `pageName` is optional (resolved by the caller, e.g.
 * from the menu's route->label map) and omitted from the payload when unknown.
 */
export function trackPageView(route: string, pageName?: string): void {
  if (route === lastRoute) return;
  lastRoute = route;
  enqueue({
    event_type: 'page_viewed',
    occurred_at: new Date().toISOString(),
    route,
    ...(pageName ? { page_name: pageName } : {}),
  });
}

/**
 * Records viewing a specific shipment (e.g. opening its detail drawer/tab),
 * identified by id rather than route. Deduplicates consecutive calls for the
 * same shipment id, independent of route-based dedup.
 */
export function trackShipmentViewed(shipmentId: string): void {
  enqueue({
    event_type: 'page_viewed',
    occurred_at: new Date().toISOString(),
    shipment_id: shipmentId,
  });
}

/**
 * Records viewing an execution leg's detail drawer. Sent as `execution_leg_id`
 * instead of `shipment_id`: the backend treats the two as mutually exclusive
 * and resolves the leg itself.
 */
export function trackExecutionLegViewed(executionLegId: string): void {
  enqueue({
    event_type: 'page_viewed',
    occurred_at: new Date().toISOString(),
    execution_leg_id: executionLegId,
  });
}

/**
 * Records a beacon event of any BeaconEventType, tagging it with the
 * current time. Which of reference_id/shipment_id a given event type needs
 * (and what their presence or absence means) is validated backend-side, not
 * enforced here — adding a new BeaconEventType needs no change to this
 * function. Never deduplicated: every call is enqueued, and the backend's
 * tracking_id keeps a resent batch from being logged twice.
 */
export function trackEvent(
  eventType: BeaconEventType,
  fields: { reference_id?: string; shipment_id?: string } = {}
): void {
  enqueue({
    event_type: eventType,
    occurred_at: new Date().toISOString(),
    ...fields,
  });
}

/**
 * Records a search once the user stops typing for SEARCH_SETTLE_MS — each
 * call replaces the held query and restarts the wait. Deduplicates against
 * the last recorded query, and an empty query (a cleared search box) drops
 * the held one so nothing is recorded. Called directly by client-side-
 * filtered dropdowns/pickers, never for backend-driven searches.
 */
export function trackSearch(query: string): void {
  const trimmed = query.trim();
  clearSearchTimer();
  if (!trimmed) {
    heldSearch = null;
    return;
  }
  heldSearch = { query: trimmed, occurredAt: new Date().toISOString() };
  searchTimer = setTimeout(commitHeldSearch, SEARCH_SETTLE_MS);
}

export function __resetActivityBeaconForTests(): void {
  if (typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('pagehide', sendPendingActivityNow);
  }
  beaconStore = null;
  beaconPersistor = null;
  lastRoute = null;
  lastSearchQuery = null;
  heldSearch = null;
  sending = false;
  clearSearchTimer();
  clearFlushTimer();
}
